/**
 * Utility functions for handling SCIM references, including stale references.
 * For converting reference values to EntityRef, use tryCreateEntityRef from types/entity/EntityRef.
 */

import { tryCreateEntityRef, type EntityRef } from '../types/entity/EntityRef';

/**
 * Safely extract ID from a reference value.
 * 
 * When skipReferenceExpansion=false, if a referenced entity has been deleted (stale reference),
 * the API will return just the ID string instead of an object. This function handles both cases:
 * - String ID (including stale references)
 * - Reference object with Id property
 * - Null/undefined (returns null)
 * 
 * @param referenceValue - The reference value (string ID, object with Id, or null/undefined)
 * @returns The ID string, or null if not found
 * 
 * @example
 * // Stale reference (deleted entity) - returns ID string directly
 * extractReferenceId("some-id") // returns "some-id"
 * 
 * // Expanded reference object
 * extractReferenceId({ Id: "some-id", DisplayName: "Name" }) // returns "some-id"
 * 
 * // Null/undefined
 * extractReferenceId(null) // returns null
 */
export function extractReferenceId(referenceValue: any): string | null {
    if (!referenceValue) {
        return null;
    }

    // If it's already a string (including stale references that return just the ID)
    if (typeof referenceValue === 'string') {
        return referenceValue.trim() || null;
    }

    // If it's an object, try to extract the ID
    if (typeof referenceValue === 'object' && referenceValue !== null) {
        // Try common ID property names
        return referenceValue.Id || referenceValue.id || referenceValue.$ref || null;
    }

    return null;
}

/**
 * Safely extract DisplayName from a reference value.
 * Falls back to ID if DisplayName is not available.
 * 
 * @param referenceValue - The reference value (string ID, object with Id/DisplayName, or null/undefined)
 * @returns The DisplayName string, ID string, or null if not found
 */
export function extractReferenceDisplayName(referenceValue: any): string | null {
    if (!referenceValue) {
        return null;
    }

    // If it's a string (stale reference or just ID), return it
    if (typeof referenceValue === 'string') {
        return referenceValue.trim() || null;
    }

    // If it's an object, try to get DisplayName first, then fall back to ID
    if (typeof referenceValue === 'object' && referenceValue !== null) {
        return referenceValue.DisplayName ||
            referenceValue.displayName ||
            referenceValue.Id ||
            referenceValue.id ||
            referenceValue.$ref ||
            null;
    }

    return null;
}

/**
 * Check if a reference value is a stale reference (just an ID string).
 * 
 * When skipReferenceExpansion=false and a referenced entity has been deleted,
 * the API returns just the ID string instead of an object.
 * 
 * @param referenceValue - The reference value to check
 * @returns true if it's a stale reference (string ID), false otherwise
 */
export function isStaleReference(referenceValue: any): boolean {
    return typeof referenceValue === 'string' && referenceValue.trim().length > 0;
}

/** Entity id for tooltips and navigation from a reference field value. */
export function extractReferenceEntityId(referenceValue: unknown): string | null {
    const id = extractReferenceId(referenceValue);
    if (!id) return null;
    const trimmed = id.trim();
    if (!trimmed || trimmed.startsWith('$.')) return null;
    return trimmed;
}

/**
 * Resolve entity id and type for in-app navigation from a generic reference field value.
 * Returns null when the value is empty or not a navigable entity reference (e.g. schema attribute id).
 */
export function resolveEntityReferenceNavigation(
    referenceValue: string | EntityRef | null | undefined,
    fallbackEntityTypes: string[] = []
): { entityId: string; entityType: string } | null {
    const entityId = extractReferenceEntityId(referenceValue);
    if (!entityId) return null;

    let entityType = '';
    if (referenceValue && typeof referenceValue === 'object') {
        const obj = referenceValue as { EntityType?: string; entityType?: string };
        entityType = (obj.EntityType ?? obj.entityType ?? '').trim();
    }
    if (!entityType) {
        const ref = tryCreateEntityRef(referenceValue as string | EntityRef);
        entityType = (ref?.entityType ?? '').trim();
    }
    if (!entityType) {
        entityType = (fallbackEntityTypes[0] ?? '').trim();
    }
    if (!entityType) return null;

    return { entityId, entityType };
}

